import React, { useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { usePaginatedService } from '@aph/common-react';
import queryString from 'query-string';
import dayjs from 'dayjs';
import { defaultPageSize } from 'global.constants';
import { parseArrayUrlParameter } from 'global.helpers';
import { IPublicationSearchFormData } from 'types/form.types';
import { setupDateForRequest } from 'helpers/form.helpers';
import {
  IUseSearchService,
  IPublicationSearchRequest,
  IPublicationSearchResponse
} from 'services';

import { PublicationSearchInner } from 'search-interfaces';

import './PublicationSearch.scss';

/**
 * BasePublicationSearchRequest
 *
 * @description Base search request for searching publications
 */
const basePublicationSearchRequest: IPublicationSearchRequest = {
  topics: [],
  publicationTypes: [],
  currentPage: 1,
  pageSize: defaultPageSize
};

/**
 * InitialFormValues
 *
 * @description Initial form values for publication search filter form
 */
const initialFormValues: IPublicationSearchFormData = {
  searchTerms: '',
  publicationTypes: [],
  topics: [],
  dateFrom: null,
  dateTo: null
};

/**
 * IPublicationSearch
 *
 * @description Component properties for the publication search component
 */
interface IPublicationSearch {
  searchService: IUseSearchService;
}

/**
 * PublicationSearch
 *
 * @description
 */
const PublicationSearch: React.FC<IPublicationSearch> = ({ searchService }) => {
  const formRef = useRef<any>();

  const {
    loading,
    currentPage,
    rowCount,
    pageSize,
    results,
    request: currentSearchRequest,
    refreshResults: refreshSearchResults,
    updatePagination,
  } = usePaginatedService<IPublicationSearchRequest, IPublicationSearchResponse>(searchService.searchPublications, basePublicationSearchRequest, false);

  // Setup the initial search results we need to first pass the URL params
  useEffect(() => {
    const parsedQueryParameters = queryString.parse(window.location.search);

    // To deal with arrays not serialising correctly with a single value we need to parse it using our custom helper
    // This means parameter=value will become ["value"] instead of "value"
    const topicsParsed = parseArrayUrlParameter(parsedQueryParameters, 'topics');
    const publicationTypesParsed = parseArrayUrlParameter(parsedQueryParameters, 'publicationTypes');
    const dateFrom = typeof parsedQueryParameters['dateFrom'] === 'string' ? dayjs(parsedQueryParameters['dateFrom']) : null;
    const dateTo = typeof parsedQueryParameters['dateTo'] === 'string' ? dayjs(parsedQueryParameters['dateTo']) : null;

    const initialPublicationSearchRequest: IPublicationSearchRequest = {
      ...basePublicationSearchRequest,
      ...parsedQueryParameters,
      topics: topicsParsed,
      publicationTypes: publicationTypesParsed
    };

    refreshSearchResults(initialPublicationSearchRequest);

    if (formRef.current) {
      formRef.current.setValues({
        ...initialFormValues,
        ...parsedQueryParameters,
        topics: topicsParsed,
        publicationTypes: publicationTypesParsed,
        dateFrom: dateFrom,
        dateTo: dateTo
      });
    }
  }, []);

  /**
   * HandleSubmitSearch
   *
   * @description Form submission handler
   * @param {IPublicationSearchFormData} values Form values
   * @returns {void}
   */
  const handleSubmitSearch = (values: IPublicationSearchFormData): void => {
    const updatedSearchRequest: IPublicationSearchRequest = {
      currentPage: 1,
      pageSize: defaultPageSize,
      topics: values.topics,
      publicationTypes: values.publicationTypes
    };

    if (values.searchTerms) {
      updatedSearchRequest.searchTerms = values.searchTerms;
    }

    updatedSearchRequest.dateFrom = setupDateForRequest(values?.dateFrom);
    updatedSearchRequest.dateTo = setupDateForRequest(values?.dateTo);

    refreshSearchResults(updatedSearchRequest);
  };

  return (
    <div>
      <Formik<IPublicationSearchFormData>
        initialValues={{
          searchTerms: '',
          publicationTypes: [],
          topics: [],
          dateFrom: null,
          dateTo: null
        }}
        // We don't need to run the submit because we are actually refreshing the results on change of each field
        onSubmit={handleSubmitSearch}
        onReset={() => {
          window.history.replaceState(null, '', '?');
          refreshSearchResults(basePublicationSearchRequest);
        }}
        validateOnChange={false}
        validateOnBlur={false}
        innerRef={formRef}
      >
        <PublicationSearchInner
          searchService={searchService}
          searchResults={{
            loading,
            results,
            currentPage,
            pageSize,
            rowCount
          }}
          currentSearchRequest={currentSearchRequest}
          paginationHandler={updatePagination}
          filterSearchResults={refreshSearchResults}
        />
      </Formik>
    </div>
  );
};

export default PublicationSearch;
